class ImageService {
  constructor() {
    // Detectar se está rodando no Vercel ou localmente
    this.isVercel = typeof window !== 'undefined' && 
      (window.location.protocol === 'https:' || 
       window.location.hostname.includes('vercel.app') || 
       window.location.hostname.includes('vercel.com') ||
       window.location.hostname.includes('gpreto.space') ||
       window.location.hostname !== 'localhost');
    
    // URL base da API
    this.apiUrl = this.isVercel 
      ? '/api'  // Vercel Functions
      : 'http://localhost:3001/api';  // Servidor local
    
  }

  async uploadFile(file) {
    // Criar AbortController para timeout (fora do try para estar disponível no catch)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 segundos
    
    try {

      // Validar o arquivo
      if (!file) {
        throw new Error('Nenhum arquivo fornecido');
      }

      // Validar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        throw new Error('Apenas arquivos de imagem são permitidos');
      }

      // Validar tamanho (máximo 25MB)
      if (file.size > 25 * 1024 * 1024) {
        throw new Error('Arquivo muito grande. Máximo 25MB permitido.');
      }

      // Criar FormData para envio
      const formData = new FormData();
      formData.append('image', file);


      // Fazer upload via API com timeout
      const response = await fetch(`${this.apiUrl}/upload-image`, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
        headers: {
          // Não definir Content-Type, deixar o browser definir com boundary
        }
      });

      clearTimeout(timeoutId);


      if (!response.ok) {
        let errorData;
        try {
          const responseText = await response.text();
          
          if (responseText) {
            errorData = JSON.parse(responseText);
          } else {
            errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
          }
        } catch (parseError) {
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        
        throw new Error(errorData.error || 'Erro no upload');
      }

      const responseText = await response.text();
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error('Resposta inválida do servidor');
      }


      // Retornar URL direta do DO Spaces (pública, sem necessidade de proxy)
      return result.imageUrl;

    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('Timeout no upload da imagem. Tente novamente.');
      }
      
      throw new Error(`Erro no upload: ${error.message}`);
    }
  }

  async deleteFile(imageUrl) {
    try {
      if (!imageUrl) {
        throw new Error('URL da imagem não fornecida');
      }

      // Extrair o nome do arquivo da URL
      let filename;
      if (imageUrl.includes('digitaloceanspaces.com/images/')) {
        filename = imageUrl.split('/images/')[1];
      } else if (imageUrl.includes('/api/image?filename=')) {
        filename = imageUrl.split('filename=')[1];
      } else if (imageUrl.includes('ideolog.ia.br/')) {
        filename = imageUrl.split('ideolog.ia.br/')[1];
      } else {
        filename = imageUrl.split('/').pop().split('?')[0];
      }

      const response = await fetch(`${this.apiUrl}/delete-image/${filename}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao deletar imagem');
      }

      return true;

    } catch (error) {
      throw new Error(`Erro ao deletar: ${error.message}`);
    }
  }

  generateFileName(originalName) {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const extension = originalName.split('.').pop();
    return `product_${timestamp}_${randomString}.${extension}`;
  }

  // Método para validar URL de imagem
  isValidImageUrl(url) {
    try {
      if (!url || typeof url !== 'string' || url.trim() === '') {
        return false;
      }
      
      const cleanUrl = url.trim();
      
      // Validar formato básico antes de tentar construir URL
      if (cleanUrl.length < 5 || (!cleanUrl.includes('.') && !cleanUrl.startsWith('/'))) {
        return false;
      }
      
      // Se é uma URL relativa, considerar válida se começar com /api/image
      if (cleanUrl.startsWith('/api/image')) {
        return true;
      }
      
      // Para URLs absolutas, tentar construir URL
      if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
        new URL(cleanUrl);
        return cleanUrl.includes('digitaloceanspaces.com') ||
               cleanUrl.includes('ideolog.ia.br') ||
               cleanUrl.includes('localhost:3001/api/image') ||
               cleanUrl.includes('/api/image');
      }
      
      return false;
    } catch {
      return false;
    }
  }

  // Método para normalizar URL de imagem
  normalizeImageUrl(url) {
    if (!url || typeof url !== 'string' || url.trim() === '') {
      return null;
    }
    
    // Se já é uma URL válida, verificar se precisa normalizar
    if (this.isValidImageUrl(url)) {
      // Se tem query parameter, converter para path parameter
      if (url.includes('/api/image?filename=')) {
        const filename = url.split('filename=')[1];
        return `${this.apiUrl}/image/${filename}`;
      }
      return url;
    }
    
    return null;
  }

  // Método para testar URLs de imagens
  async testImageUrls(imageUrls) {
    const results = [];
    
    for (const url of imageUrls) {
      try {
        
        const processedUrl = this.getImageUrl(url);
        
        if (!processedUrl) {
          results.push({ url, processedUrl: null, status: 'invalid', error: 'URL inválida' });
          continue;
        }
        
        // Testar se a imagem carrega
        const response = await fetch(processedUrl, { method: 'HEAD' });
        const status = response.ok ? 'ok' : 'error';
        const error = response.ok ? null : `HTTP ${response.status}: ${response.statusText}`;
        
        results.push({ url, processedUrl, status, error });
        
      } catch (error) {
        results.push({ url, processedUrl: null, status: 'error', error: error.message });
      }
    }
    
    return results;
  }

  // Método para obter URL de imagem otimizada
  getImageUrl(imageUrl) {
    
    // Validar entrada mais rigorosamente
    if (!imageUrl || typeof imageUrl !== 'string' || imageUrl.trim() === '') {
      return null;
    }
    
    // Limpar a URL de espaços e caracteres inválidos
    const cleanUrl = imageUrl.trim();
    if (cleanUrl === '') {
      return null;
    }
    
    // Verificar se contém caracteres perigosos que podem quebrar o construtor URL
    if (cleanUrl.includes('\n') || cleanUrl.includes('\r') || cleanUrl.includes('\t')) {
      return null;
    }
    
    try {
      // Se já é uma URL completa e válida, retornar diretamente
      if (cleanUrl.startsWith('http://') || cleanUrl.startsWith('https://')) {
        try {
          // Validar se a URL tem formato básico válido antes de tentar construir
          if (cleanUrl.length < 10 || !cleanUrl.includes('.')) {
            return null;
          }
          
          // Tentar construir URL para validar
          new URL(cleanUrl);
          return cleanUrl;
        } catch (urlError) {
          // Se a URL é inválida mas parece ser uma URL de imagem, tentar corrigir
          if (cleanUrl.includes('.jpg') || cleanUrl.includes('.jpeg') || cleanUrl.includes('.png') || cleanUrl.includes('.gif')) {
            // Tentar usar encodeURI para corrigir caracteres especiais
            try {
              const fixedUrl = encodeURI(cleanUrl);
              new URL(fixedUrl);
              return fixedUrl;
            } catch (fixError) {
              return null;
            }
          }
          return null;
        }
      }
      
      // Se já é uma URL relativa válida, retornar diretamente
      if (cleanUrl.startsWith('/api/image/') || cleanUrl.startsWith('/api/image?')) {
        return cleanUrl;
      }
      
      // Se é um nome de arquivo, construir URL
      let constructedUrl;
      if (this.isVercel) {
        // Para Vercel, usar URL relativa para a própria API
        constructedUrl = `/api/image/${cleanUrl}`;
      } else {
        // Para desenvolvimento local, usar URL completa
        constructedUrl = `${this.apiUrl}/image/${cleanUrl}`;
      }
      
      // Validar se a URL construída é válida
      if (constructedUrl.startsWith('/')) {
        // URL relativa - válida para uso direto
        return constructedUrl;
      } else if (constructedUrl.startsWith('http://') || constructedUrl.startsWith('https://')) {
        // URL absoluta - validar com construtor URL
        try {
          // Validar formato básico antes de tentar construir URL
          if (constructedUrl.length < 15 || !constructedUrl.includes('.')) {
            return null;
          }
          
          new URL(constructedUrl);
          return constructedUrl;
        } catch (urlError) {
          return null;
        }
      } else {
        // URL malformada
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  // Método para pré-carregar imagens
  async preloadImage(imageUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageUrl;
    });
  }

  // Método para verificar se a imagem existe no servidor
  // Método para testar upload com arquivo de teste
  async testUpload() {
    try {
      
      // Criar um arquivo de teste (1x1 pixel PNG)
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, 1, 1);
      
      return new Promise((resolve, reject) => {
        canvas.toBlob(async (blob) => {
          try {
            const testFile = new File([blob], 'test-image.png', { type: 'image/png' });
            
            const result = await this.uploadFile(testFile);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }, 'image/png');
      });
    } catch (error) {
      throw error;
    }
  }

  async checkImageExists(imageUrl) {
    try {
      const response = await fetch(imageUrl, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      return false;
    }
  }
}

const imageService = new ImageService();
export default imageService;